import { useMemo } from 'react';
import { BoxProps, Breakpoint, StackProps } from '@mui/material';
import { v4 } from 'uuid';

import { ImageType } from '@models/Image';
import { TMenuItemPreview, TPageSectionItemPreview } from '@models/LayoutPreview';
import { SectionStyleSegment, SectionStyleSegments, SEE_ALL } from '@models/PageSection';
import { getEditorialImgUrl, getTitleImgUrl } from '@utils/imageUtils';

import previewDefs from './PagePreview/utils';

export const isGenre = (pattern: string) => pattern?.split(',').at(0) === 'genre';
export const isPoster = (pattern: string) => pattern?.split(',').at(0)?.startsWith('poster');

export const isPromo = (pattern: string) => pattern?.split(',').at(0)?.startsWith('promo');
export const isPromoWide = (pattern: string) => pattern?.split(',').at(0) === 'promo-wide';

export const isPromoWideLarge = (pattern: string) =>
  pattern?.split(',').at(0) === 'promo-wide-large';

const isStyleSegment = (styleSegment?: string): styleSegment is SectionStyleSegment =>
  SectionStyleSegments.includes(styleSegment as SectionStyleSegment);

export const getPatternSegment = (pattern: string, index: number): SectionStyleSegment => {
  const split = pattern.replace(',-', '').split(',');

  if (split.length > 1) {
    if (index < split.length) return split.at(index) as SectionStyleSegment;
    if (pattern.endsWith('-')) return split.at(-1) as SectionStyleSegment;

    const patternIndex = index - split.length * Math.floor(index / split.length);
    const value = split.at(patternIndex);

    if (isStyleSegment(value)) return value;
  }

  const value = split.at(0);
  if (isStyleSegment(value)) return value;
  return 'wide';
};

export const getPreviewTileLabel = (
  item: TPageSectionItemPreview,
  styleSegment: SectionStyleSegment
): string | undefined => {
  const { hasTitle } = previewDefs[styleSegment];

  if (!hasTitle && item.params?.behavior !== SEE_ALL) return;

  if (item.title?.type === 'episode')
    return `${item.title.episode.sequenceNumber}. ${item.title.titleName}`;

  if (item.name) return item.name;

  switch (item.type) {
    case 'title':
      return item.title?.titleName;
    case 'unmanaged':
    case 'link':
    case 'collection':
    case 'menu':
    case 'page':
    case 'image':
    default:
      return;
  }
};

export const getPreviewTileSubtitle = (
  item: TPageSectionItemPreview,
  styleSegment: SectionStyleSegment
) => {
  const { hasSubtitle } = previewDefs[styleSegment];

  if (!hasSubtitle || item.params?.behavior === SEE_ALL) return;

  return hasSubtitle
    ? item.title?.type === 'episode' && item.title
      ? item.title.originalTitle
      : item.title?.genres?.at(0)
    : undefined;
};

export const PreviewContentWrapperProps: BoxProps | StackProps = {
  padding: { xs: '0 3%', md: '0 6%' },
};

export const previewSpacingMap: Record<Breakpoint, number> = { xs: 2, sm: 2, md: 2, lg: 3, xl: 3 };

export const getPreviewImgUrl = (
  item: TMenuItemPreview | TPageSectionItemPreview,
  imageType: ImageType | undefined = 'wide',
  styleSegment?: SectionStyleSegment,
  width?: number,
  hash?: string
) => {
  if (item.image) {
    const url = getEditorialImgUrl(item.image, imageType, { width });
    return hash ? `${getEditorialImgUrl(item.image, imageType, { width })}&hash=${hash}` : url;
  }

  if (styleSegment === 'genre') return;

  if (item.type === 'title' && item.title && item.id)
    return item.title.type === 'episode'
      ? getTitleImgUrl(item.title.series.masterId, imageType, { width })
      : getTitleImgUrl(item.id, imageType, { width });

  return;
};

export const usePreviewImgUrl = (
  item: TMenuItemPreview | TPageSectionItemPreview,
  imageType: ImageType | undefined = 'wide',
  styleSegment?: SectionStyleSegment,
  width?: number
) => {
  const hash = useMemo(() => v4(), []);
  return getPreviewImgUrl(item, imageType, styleSegment, width, hash);
};
